import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ApiService } from 'src/app/Services/api.service';
import { StorageService } from 'src/app/Services/storage.service';
import { SweetAlert } from 'src/app/Services/sweetalet.service';
import { LocationPickupComponent } from '../../directives/location-pickup/location-pickup.component';

@Component({
  selector: 'app-add-zone',
  templateUrl: './add-zone.component.html',
  styleUrls: ['./add-zone.component.scss']
})
export class AddZoneComponent implements OnInit {

  public addZoneForm: FormGroup;
  public nodalForm: FormGroup;
  public showNodalModal: boolean = false;

  @ViewChild(LocationPickupComponent) locationPickup: LocationPickupComponent;

  public formSubmit: boolean = false;
  public nodalFormSubmit: boolean = false;

  public nodalPoints: any[] = [];
  public nodalLocation: any = {};
  public editingNodalIndex: number | null = null;
  // seed to assign stable initial order for nodal points
  private nodalOrderSeed: number = 0;

  public fieldOptions = {
    branch: [],
    transitMode: [
      { value: 'pickup', name: 'Pickup' },
      { value: 'drop', name: 'Drop' }
    ]
  };

  public branchInformation: any[] = [];

  constructor(
    public api: ApiService,
    public swal: SweetAlert,
    public storage: StorageService,
    public router: Router
  ) { }

  ngOnInit(): void {
    this.loadZoneForm();
    this.loadNodalForm();
    this.getBranchList();
  }

  loadZoneForm() {
    this.addZoneForm = new FormGroup({
      zoneName: new FormControl('', [Validators.required]),
      branch: new FormControl('', [Validators.required]),
      // transitMode: new FormControl('', [Validators.required])
    });
  }

  loadNodalForm() {
    this.nodalForm = new FormGroup({
      area: new FormControl('', [Validators.required]),
      location: new FormControl('', [Validators.required])
    });
  }

  /* Branch list */
  getBranchList() {
    const obj = {
      orgID: this.storage.get('userInfo').orgID
    };
    this.api.transportManagerBranchList(obj).then((res: any) => {
      if (res.status == 'success') {
        this.branchInformation = res.data;
        this.addBranchOptions();
      }
    }).catch((err) => {
      console.log(err);
    });
  }

  addBranchOptions() {
    this.fieldOptions.branch = [];
    if (this.branchInformation && this.branchInformation.length > 0) {
      this.branchInformation.filter((e) => {
        if (!(e?.isNodal || e?.isNodalBranch || e?.nodalBranch)) {
          return;
        }
        const option = { name: e.branchName, value: e.branchID };
        this.fieldOptions.branch.push(option);
      });
    }
  }

  /* Nodal point helpers */
  getNodalLocation(obj: any) {
    this.nodalLocation = { ...obj };
  }

  openNodalModal() {
    this.showNodalModal = true;
  }
  resetNodalForm() {
    this.nodalForm.reset();
    this.nodalLocation = {};
    this.nodalFormSubmit = false;
    this.editingNodalIndex = null;
    // Also reset the location map/picker state so it starts fresh next time
    if (this.locationPickup) {
      this.locationPickup.resetLocationState();
    }
  }

  addNodalPoint() {
    this.nodalFormSubmit = true;
    if (this.nodalForm.valid) {
      const value = this.nodalForm.value;

      // ensure nodalLocation exists and update its name from the control value
      this.nodalLocation = {
        ...(this.nodalLocation || {}),
        name: value.location
      };

      let node: any = {
        area: value.area,
        location: { ...this.nodalLocation }
      };

      if (this.editingNodalIndex !== null && this.editingNodalIndex >= 0) {
        // preserve original order index when editing an existing nodal point
        const existing = this.nodalPoints[this.editingNodalIndex];
        node.initialIndex = existing && existing.initialIndex != null ? existing.initialIndex : this.editingNodalIndex;
        this.nodalPoints[this.editingNodalIndex] = node;
      } else {
        // assign a stable initial index for new nodal points
        node.initialIndex = ++this.nodalOrderSeed;
        this.nodalPoints.push(node);
      }

      this.resetNodalForm();
      this.closeNodalModal(); 
    }
  }

  /**
   * Reorder nodal points after drag & drop
   */
  dropNodalPoint(event: CdkDragDrop<any[]>) {
    if (!event || event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.nodalPoints, event.previousIndex, event.currentIndex);
  }

  removeNodalPoint(index: number) {
    this.nodalPoints.splice(index, 1);
  }

  editNodalPoint(index: number) {
    const point = this.nodalPoints[index];
    if (!point) { return; }

    this.editingNodalIndex = index;
    // show current values in the modal form
    this.nodalForm.patchValue({
      area: point.area,
      location: point.location?.name || ''
    });
    // keep full location object separately for saving
    this.nodalLocation = {
      lat: point.location?.lat,
      lng: point.location?.lng,
      name: point.location?.name || point.area || ''
    };
    this.nodalFormSubmit = false;
    this.openNodalModal();
  }

  closeNodalModal() {
    this.showNodalModal = false;
  }

  // Reset nodal points order back to initial insertion order (do not clear items)
  resetNodalPoints() {
    if (!this.nodalPoints || this.nodalPoints.length === 0) {
      return;
    }
    // Ensure every item has an initialIndex, fallback to its current index once
    this.nodalPoints = this.nodalPoints
      .map((p, idx) => ({
        ...p,
        initialIndex: p.initialIndex != null ? p.initialIndex : idx
      }))
      .sort((a, b) => (a.initialIndex || 0) - (b.initialIndex || 0));
  }

  /* checking error in main form fields*/
  public checkError = (controlName: string, errorName: string) => {
    return this.addZoneForm && this.addZoneForm.controls[controlName].hasError(errorName);
  }

  /* checking error in nodal form fields*/
  public checkNodalError = (controlName: string, errorName: string) => {
    return this.nodalFormSubmit && this.nodalForm && this.nodalForm.controls[controlName].hasError(errorName);
  }

  /* Cancel Add Zone */
  cancel() {
    this.router.navigate(['/organization/transport-manager/information/orgInfo/zone/view-zone']);
  }

  /* Submit Zone */
  onSubmit() {
    this.formSubmit = true;

    if (!this.addZoneForm.valid) {
      return;
    }

    if (!this.nodalPoints || this.nodalPoints.length === 0) {
      this.swal.swalFunction('warning', 'Validation', 'Please add at least one nodal point.', 3000);
      return;
    }

    const formValue = this.addZoneForm.value;

    const payload: any = {
      zoneName: formValue.zoneName,
      branch: formValue.branch,
      // transitMode: (formValue.transitMode || '').toLowerCase(),
      nodalPoints: this.nodalPoints.map((point, index) => ({
        name: point.area || (point.location && point.location.name) || '',
        location: {
          lat: point.location?.lat,
          lng: point.location?.lng
        },
        sequenceNo: index + 1
      }))
    };

    this.api.transportManagerZoneCreate(payload).then((res: any) => {
      if (res && res.status === 'success') {
        this.swal.swalFunction('success', res.status, res.message, 2000);
        this.router.navigate(['/organization/transport-manager/information/orgInfo/zone/view-zone']);
      } else {
        this.swal.swalFunction('error', res?.status || 'error', res?.message || 'Failed to create zone.', 3000);
      }
    }).catch((err) => {
      console.log(err);
      this.swal.swalFunction('error', 'error', 'Something went wrong while creating zone.', 3000);
    });
  }

}
